import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchYearbook, fetchChildren, yearbookPdfUrl } from "@/lib/api";
import { BookHeart, Loader2, ChevronLeft, ChevronRight, Download, Trophy, Mail, CalendarClock } from "lucide-react";
import PageErrorBoundary from "@/components/PageErrorBoundary";
import useTrackPage from "@/hooks/useTrackPage";

function YearbookInner() {
  useTrackPage("yearbook_open");
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [childId, setChildId] = useState("");
  const [children, setChildren] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChildren()
      .then((rows) => setChildren((rows || []).filter((c) => !c.is_demo)))
      .catch(() => setChildren([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchYearbook(year, childId || undefined)
      .then(setData).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year, childId]);

  return (
    <div data-testid="yearbook-page" className="space-y-6">
      <header className="fcc-card p-6 sm:p-8">
        <p className="label-overline flex items-center gap-2"><BookHeart className="w-3 h-3" /> Family Yearbook</p>
        <h1 className="font-display text-3xl text-[var(--fcc-ink)] mt-2">Your family year, month by month</h1>
        <p className="text-sm text-[var(--fcc-ink-soft)] mt-2 max-w-2xl">
          A year-long chronicle of milestones, achievements, and the small moments that matter. Download a print-friendly PDF keepsake.
        </p>
        <div className="mt-5 flex flex-wrap gap-2 items-center">
          <button
            data-testid="yearbook-prev"
            onClick={() => setYear(year - 1)}
            className="p-2 rounded-lg border border-[var(--fcc-border)]"
            aria-label="Previous year"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span data-testid="yearbook-year-label" className="font-display text-lg text-[var(--fcc-ink)] px-3">
            {year}
          </span>
          <button
            data-testid="yearbook-next"
            onClick={() => setYear(year + 1)}
            className="p-2 rounded-lg border border-[var(--fcc-border)]"
            aria-label="Next year"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          {children.length > 0 && (
            <select
              data-testid="yearbook-child-select"
              value={childId}
              onChange={(e) => setChildId(e.target.value)}
              className="fcc-input text-sm py-2 px-3 ml-2"
            >
              <option value="">All family</option>
              {children.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <a
            data-testid="yearbook-download-pdf"
            href={yearbookPdfUrl(year, childId || undefined)}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--fcc-green)] text-white text-sm font-semibold hover:bg-[var(--fcc-green-dark)] transition"
          >
            <Download className="w-4 h-4" /> Download Yearbook PDF
          </a>
        </div>
      </header>

      {loading ? (
        <div className="fcc-card p-12 text-center text-[var(--fcc-muted)]">
          <Loader2 className="w-5 h-5 mx-auto animate-spin" />
          <p className="text-sm mt-3">Assembling {year}…</p>
        </div>
      ) : !data ? (
        <div className="fcc-card p-10 text-center text-sm text-[var(--fcc-muted)]">Could not load this year.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="yearbook-months-grid">
          {data.months.map((m) => (
            <MonthCard
              key={m.month}
              year={year}
              month={m.month}
              monthLabel={m.month_label}
              narrative={m.narrative}
              milestones={m.milestones || []}
              stats={m.stats || {}}
              childId={childId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MonthCard({ year, month, monthLabel, narrative, milestones, stats, childId }) {
  const hasContent = (narrative && narrative.length > 0) || milestones.length > 0 || (stats.emails || 0) + (stats.events || 0) > 0;
  const cid = childId ? `&child_id=${childId}` : "";
  return (
    <article
      data-testid={`yearbook-month-${month}`}
      className={`fcc-card p-5 flex flex-col ${hasContent ? "" : "opacity-70"}`}
    >
      <header className="flex items-baseline justify-between mb-2">
        <h3 className="font-display text-xl text-[var(--fcc-ink)]">{monthLabel}</h3>
        <Link
          to={`/app/family-story?y=${year}&m=${month}${cid}`}
          className="text-[10px] uppercase tracking-widest text-[var(--fcc-green)] font-semibold hover:underline"
          data-testid={`yearbook-open-story-${month}`}
        >
          Open →
        </Link>
      </header>
      {narrative ? (
        <p className="text-sm text-[var(--fcc-ink-soft)] leading-relaxed line-clamp-5">{narrative}</p>
      ) : (
        <p className="text-xs text-[var(--fcc-muted)] italic">
          {hasContent ? "Activity recorded — story not yet generated." : "Quiet month — no entries."}
        </p>
      )}
      {milestones.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid={`yearbook-milestones-${month}`}>
          {milestones.slice(0, 3).map((mil, idx) => (
            <li key={idx} className="text-xs text-[var(--fcc-ink)] flex items-start gap-2">
              <Trophy className="w-3 h-3 text-[var(--fcc-green)] mt-0.5 shrink-0" />
              <span className="truncate">{mil.label}</span>
            </li>
          ))}
          {milestones.length > 3 && (
            <li className="text-[10px] text-[var(--fcc-muted)] uppercase tracking-widest">
              +{milestones.length - 3} more
            </li>
          )}
        </ul>
      )}
      <div className="mt-auto pt-3 flex flex-wrap gap-1.5 border-t border-[var(--fcc-border)] mt-3">
        <Stat Icon={Trophy} value={stats.milestones} label="milestones" />
        <Stat Icon={Mail} value={stats.emails} label="emails" />
        <Stat Icon={CalendarClock} value={stats.events} label="events" />
      </div>
    </article>
  );
}

function Stat({ Icon, value, label }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">
      <Icon className="w-3 h-3" />
      {value ?? 0} {label}
    </span>
  );
}

export default function YearbookPage() {
  return (
    <PageErrorBoundary scope="yearbook">
      <YearbookInner />
    </PageErrorBoundary>
  );
}
